<template>
  <!-- w-full so the field fills its container on its own; with inheritAttrs:false
       a parent's w-full lands on the inner control, not this root. See BaseInput. -->
  <div class="w-full" v-bind="rootAttrs">
    <FloatLabel
      variant="on"
      class="relative transition-colors duration-200 !font-just w-full block"
      :aria-label="props.ariaLabel || (label ? `Select-${label}` : '')"
    >
      <Select
        v-bind="controlAttrs"
        :modelValue="internalValue"
        :options="options"
        :optionLabel="optionLabel"
        :optionValue="optionValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :inputId="id"
        :name="name"
        :aria-labelledby="`${id}-label`"
        :aria-label="ariaLabel || (label ? `Select-${label}` : '')"
        :data-test-id="`${id}-select`"
        @update:modelValue="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
        class="bg-transparent !border-[2px] !rounded-[8px] !px-[14px] !py-[8.5px] !text-labelColor !font-just !text-base"
        :class="[inputClasses, width]"
        :showClear="showClear"
        :pt="{
          input: { class: 'text-left w-full' },
          label: { class: '!text-left !w-full' },
          item: { class: '!text-left !font-just' },
          panel: { class: '!text-left !font-just' },
          filterInput: { class: '!text-left' },
          optionlabel: { class: '!font-just' },
        }"
      >
        <template #dropdownicon>
          <span
            v-html="dropdownCaret"
            class="dropdown-icon flex items-center !font-just"
          ></span>
        </template>
        <template #option="slotProps">
          <div
            :data-test-id="`${id}-option-${slotProps.option[optionValue]}`"
            class="font-just"
          >
            {{ slotProps.option[optionLabel] }}
          </div>
        </template>
      </Select>

      <label
        :id="`${id}-label`"
        :for="id"
        class="absolute left-2 -top-2 !font-just !text-base !font-400 pointer-events-none transition-all duration-200 flex gap-1"
        :class="[labelClasses, labelClass]"
        :data-test-id="`${id}-label`"
      >
        <template v-if="required">
          {{ label }}<span v-if="focused || !internalValue">*</span>
        </template>
        <template v-else>
          {{ label }}
        </template>
      </label>
    </FloatLabel>

    <Message
      v-if="invalid && errorMessage"
      severity="error"
      size="small"
      variant="simple"
      :class="{
        '!text-error  !px-[10px] !mt-[3.84px] !font-robo !font-400 !text-[15.36px]':
          invalid,
      }"
      :data-test-id="`${id}-errorMessage`"
    >
      {{ errorMessage }}
    </Message>
  </div>
</template>

<script setup>
// Forward fallthrough attrs (tabindex, @focus/@click handlers, etc.) onto the
// inner <Select> via v-bind="$attrs" below, NOT onto the wrapper <div>. Without
// this, a tabindex="0" passed by a parent (e.g. CustomField) lands on the root
// div as well, creating two tab stops per field. See CustomField.vue.
defineOptions({ inheritAttrs: false });

import { ref, computed, watch } from "vue";
import { dropdownCaret } from "@/utils/commonSvg";
import { useControlAttrs } from "@/composables/useControlAttrs";

// class/style -> root (grid col-span, width); the rest -> inner control.
const { rootAttrs, controlAttrs } = useControlAttrs();

const props = defineProps({
  modelValue: [String, Number, Object],
  label: String,
  labelClass: {},
  id: String,
  name: {
    type: String,
    default: "select",
  },
  options: {
    type: Array,
    default: () => [],
  },
  optionLabel: {
    type: String,
    default: "label",
  },
  optionValue: {
    type: String,
    default: "value",
  },
  placeholder: {
    type: String,
    default: "",
  },
  required: Boolean,
  errorMessage: String,
  invalid: Boolean,
  width: { type: String, default: "w-full" },
  disabled: { type: Boolean, default: false },
  showClear: { type: Boolean, default: false },
  ariaLabel: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue", "change", "blur"]);
const focused = ref(false);
const internalValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== internalValue.value) internalValue.value = val;
  }
);

const handleChange = (value) => {
  internalValue.value = value;
  emit("update:modelValue", value);
  emit("change", value);
};

const handleFocus = () => {
  focused.value = true;
};

const handleBlur = (event) => {
  focused.value = false;
  emit("blur", event);
};

const getState = () => {
  if (props.invalid) return "invalid";
  if (focused.value || internalValue.value) return "active";
  return "default";
};

const inputClasses = computed(() => {
  const state = getState();
  return {
    "!border-error": state === "invalid",
    "!border-deepSky": state === "active",
    "!border-softGray": state === "default",
  };
});

const labelClasses = computed(() => {
  const state = getState();
  return {
    "!text-error": state === "invalid",
    "!text-deepSky": state === "active",
    "!text-greyBlue": state === "default",
  };
});
</script>